import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  Check,
  X,
  BadgeCheck,
  Flag,
  Users,
  ExternalLink,
  User,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user: currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [reports, setReports] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appsRes, reportsRes, membersRes] = await Promise.all([
        axios.get(`${API}/admin/applications`, { withCredentials: true }),
        axios.get(`${API}/admin/reports`, { withCredentials: true }),
        axios.get(`${API}/admin/members`, { withCredentials: true }),
      ]);
      setApplications(appsRes.data);
      setReports(reportsRes.data);
      setMembers(membersRes.data);
    } catch (error) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveApplication = async (userId) => {
    try {
      await axios.post(
        `${API}/admin/applications/${userId}/approve`,
        {},
        { withCredentials: true }
      );
      toast.success("Application approved!");
      fetchData();
    } catch (error) {
      toast.error("Failed to approve application");
    }
  };

  const handleRejectApplication = async (userId) => {
    try {
      await axios.post(
        `${API}/admin/applications/${userId}/reject`,
        {},
        { withCredentials: true }
      );
      toast.success("Application rejected");
      fetchData();
    } catch (error) {
      toast.error("Failed to reject application");
    }
  };

  const handleVerifyMember = async (userId) => {
    try {
      await axios.post(
        `${API}/admin/users/${userId}/verify`,
        {},
        { withCredentials: true }
      );
      toast.success("Member verified!");
      fetchData();
    } catch (error) {
      toast.error("Failed to verify member");
    }
  };

  const handleResolveReport = async (reportId) => {
    try {
      await axios.post(
        `${API}/admin/reports/${reportId}/resolve`,
        {},
        { withCredentials: true }
      );
      toast.success("Report resolved");
      fetchData();
    } catch (error) {
      toast.error("Failed to resolve report");
    }
  };

  return (
    <div className="min-h-screen bg-ivory">
      {/* Header */}
      <header className="border-b border-border bg-ivory/80 backdrop-blur-md sticky top-0 z-50">
        <div className="container-luxury flex items-center justify-between h-20">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-charcoal hover:text-gold transition-colors"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-caption">Back to Dashboard</span>
          </button>
          <h1 className="font-serif text-xl text-charcoal">Admin Dashboard</h1>
          <Button variant="ghost" onClick={logout} className="btn-ghost" data-testid="logout-btn">
            Sign Out
          </Button>
        </div>
      </header>

      <main className="container-luxury py-12">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white border border-border p-8">
            <p className="text-caption mb-2">Pending Applications</p>
            <p className="text-h1 text-charcoal">{applications.length}</p>
          </div>
          <div className="bg-white border border-border p-8">
            <p className="text-caption mb-2">Open Reports</p>
            <p className="text-h1 text-charcoal">
              {reports.filter((r) => r.status === "pending").length}
            </p>
          </div>
          <div className="bg-white border border-border p-8">
            <p className="text-caption mb-2">Total Members</p>
            <p className="text-h1 text-charcoal">{members.length}</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="applications">
          <TabsList className="bg-transparent border-b border-border rounded-none p-0 h-auto w-full justify-start">
            <TabsTrigger
              value="applications"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold data-[state=active]:bg-transparent px-6 py-3 text-sm uppercase tracking-widest font-mono"
              data-testid="tab-applications"
            >
              Applications ({applications.length})
            </TabsTrigger>
            <TabsTrigger
              value="reports"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold data-[state=active]:bg-transparent px-6 py-3 text-sm uppercase tracking-widest font-mono"
              data-testid="tab-reports"
            >
              Reports ({reports.filter((r) => r.status === "pending").length})
            </TabsTrigger>
            <TabsTrigger
              value="members"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold data-[state=active]:bg-transparent px-6 py-3 text-sm uppercase tracking-widest font-mono"
              data-testid="tab-members"
            >
              Members ({members.length})
            </TabsTrigger>
          </TabsList>

          {/* Applications Tab */}
          <TabsContent value="applications" className="mt-8">
            {loading ? (
              <div className="text-center py-16">
                <p className="text-caption">Loading...</p>
              </div>
            ) : applications.length === 0 ? (
              <div className="text-center py-16">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" strokeWidth={1} />
                <p className="text-body-lg">No pending applications</p>
              </div>
            ) : (
              <ScrollArea className="h-[600px]">
                <div className="space-y-6">
                  {applications.map((app) => (
                    <div
                      key={app.user_id}
                      className="bg-white border border-border p-8"
                      data-testid={`application-${app.user_id}`}
                    >
                      <div className="flex items-start justify-between mb-6">
                        <div className="flex items-center gap-4">
                          {app.picture ? (
                            <img
                              src={app.picture}
                              alt={app.name}
                              className="w-16 h-16 object-cover"
                            />
                          ) : (
                            <div className="w-16 h-16 bg-secondary flex items-center justify-center">
                              <User className="w-8 h-8 text-muted-foreground" />
                            </div>
                          )}
                          <div>
                            <h3 className="text-h3 text-charcoal">{app.name}</h3>
                            <p className="text-body text-muted-foreground">
                              {app.profession}
                            </p>
                            {app.location && (
                              <p className="text-caption">{app.location}</p>
                            )}
                          </div>
                        </div>
                        <a
                          href={app.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-gold hover:underline text-sm"
                          data-testid={`linkedin-${app.user_id}`}
                        >
                          LinkedIn <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>

                      <div className="mb-6">
                        <p className="text-caption mb-2">Statement of Intent</p>
                        <p className="text-body">{app.statement_of_intent}</p>
                      </div>

                      {app.expertise?.length > 0 && (
                        <div className="mb-4">
                          <p className="text-caption mb-2">Expertise</p>
                          <div className="flex flex-wrap gap-2">
                            {app.expertise.map((item, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-1 bg-secondary text-xs font-mono"
                              >
                                {item}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex gap-4 mt-6">
                        <Button
                          onClick={() => handleApproveApplication(app.user_id)}
                          className="btn-primary inline-flex items-center gap-2"
                          data-testid={`approve-${app.user_id}`}
                        >
                          <Check className="w-4 h-4" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => handleRejectApplication(app.user_id)}
                          variant="outline"
                          className="btn-secondary inline-flex items-center gap-2"
                          data-testid={`reject-${app.user_id}`}
                        >
                          <X className="w-4 h-4" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="mt-8">
            {loading ? (
              <div className="text-center py-16">
                <p className="text-caption">Loading...</p>
              </div>
            ) : reports.length === 0 ? (
              <div className="text-center py-16">
                <Flag className="w-12 h-12 text-muted-foreground mx-auto mb-4" strokeWidth={1} />
                <p className="text-body-lg">No reports</p>
              </div>
            ) : (
              <ScrollArea className="h-[600px]">
                <div className="space-y-6">
                  {reports.map((report) => (
                    <div
                      key={report.report_id}
                      className={`bg-white border p-8 ${
                        report.status === "pending"
                          ? "border-destructive/30"
                          : "border-border"
                      }`}
                      data-testid={`report-${report.report_id}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <p className="text-caption mb-1">
                            Reported by: {report.reporter_name}
                          </p>
                          <h3 className="text-h3 text-charcoal">
                            {report.reported_user_name}
                          </h3>
                        </div>
                        <span
                          className={`px-3 py-1 text-xs font-mono uppercase ${
                            report.status === "pending"
                              ? "bg-destructive/10 text-destructive"
                              : "bg-secondary text-muted-foreground"
                          }`}
                        >
                          {report.status}
                        </span>
                      </div>

                      <div className="mb-4">
                        <p className="text-caption mb-1">Reason</p>
                        <p className="text-body font-medium">{report.reason}</p>
                      </div>

                      <div className="mb-6">
                        <p className="text-caption mb-1">Details</p>
                        <p className="text-body">{report.details}</p>
                      </div>

                      {report.status === "pending" && (
                        <Button
                          onClick={() => handleResolveReport(report.report_id)}
                          className="btn-primary"
                          data-testid={`resolve-${report.report_id}`}
                        >
                          Mark as Resolved
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members" className="mt-8">
            {loading ? (
              <div className="text-center py-16">
                <p className="text-caption">Loading...</p>
              </div>
            ) : members.length === 0 ? (
              <div className="text-center py-16">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" strokeWidth={1} />
                <p className="text-body-lg">No members yet</p>
              </div>
            ) : (
              <ScrollArea className="h-[600px]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {members
                    .filter(
                      (m) =>
                        m.application_status === "approved" &&
                        m.payment_status === "completed"
                    )
                    .map((member) => (
                      <div
                        key={member.user_id}
                        className="bg-white border border-border p-6 flex items-center justify-between"
                        data-testid={`member-${member.user_id}`}
                      >
                        <div className="flex items-center gap-4">
                          {member.picture ? (
                            <img
                              src={member.picture}
                              alt={member.name}
                              className="w-12 h-12 object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-secondary flex items-center justify-center">
                              <User className="w-6 h-6 text-muted-foreground" />
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-charcoal">
                                {member.name}
                              </span>
                              {member.is_verified && (
                                <BadgeCheck className="w-4 h-4 text-gold" />
                              )}
                            </div>
                            <p className="text-caption">{member.profession}</p>
                          </div>
                        </div>

                        {!member.is_verified && (
                          <Button
                            onClick={() => handleVerifyMember(member.user_id)}
                            variant="outline"
                            size="sm"
                            className="btn-ghost inline-flex items-center gap-1"
                            data-testid={`verify-${member.user_id}`}
                          >
                            <BadgeCheck className="w-4 h-4" />
                            Verify
                          </Button>
                        )}
                      </div>
                    ))}
                </div>
              </ScrollArea>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
